import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useChat } from '../context/ChatContext';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import ChatMessage from '../components/ChatMessage';

import WelcomeMessage from '../components/WelcomeMessage';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Sheet, SheetContent } from '../components/ui/sheet';
import { Menu, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChatPage = () => {
  const { user, token } = useAuth();
  const {
    messages,
    activeConversation,
    loading,
    sendingMessage,
    fetchConversations,
    sendMessage,
    startNewChat
  } = useChat();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [quickActions, setQuickActions] = useState([]);
  const [selectedArea, setSelectedArea] = useState(null);
  const messagesEndRef = useRef(null);

  // Fetch quick actions una vez y derivar áreas disponibles
  const fetchQuickActions = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/ai-config/quick-actions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setQuickActions(data);
        // Seleccionar la primera área disponible por defecto
        if (data.length > 0) {
          const firstArea = data[0].area || 'general';
          setSelectedArea(prev => prev ?? firstArea);
        }
      }
    } catch (e) {
      console.error('Error fetching quick actions:', e);
    }
  }, [token]);

  useEffect(() => {
    fetchQuickActions();
  }, [fetchQuickActions]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    // Scroll to bottom when new messages arrive
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (message, actionId = null) => {
    try {
      await sendMessage(message, null, actionId);
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleQuickAction = async (promptText, actionId) => {
    await handleSendMessage(promptText, actionId);
  };

  const handleAreaSelect = (area) => {
    setSelectedArea(area);
    startNewChat();
  };

  return (
    <div className="flex h-screen bg-[#F8F9FA]" data-testid="chat-page">
      {/* Desktop Sidebar - Always visible */}
      <div className="hidden md:block">
        <Sidebar
          quickActions={quickActions}
          selectedArea={selectedArea}
          onAreaSelect={handleAreaSelect}
        />
      </div>

      {/* Mobile Menu - Slide out panel */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="p-0 w-72">
          <Sidebar
            quickActions={quickActions}
            selectedArea={selectedArea}
            onAreaSelect={(area) => { handleAreaSelect(area); setMobileMenuOpen(false); }}
            onSelectConversation={() => setMobileMenuOpen(false)}
          />
        </SheetContent>
      </Sheet>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-14 border-b bg-white flex items-center px-4 gap-3">
          {/* Mobile menu button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMobileMenuOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-5 w-5" />
          </Button>

          <div className="flex-1">
            <h1 className="font-semibold font-['Space_Grotesk'] text-lg truncate">
              {activeConversation?.title || ''}
            </h1>
            {activeConversation?.scope_type && activeConversation.scope_type !== 'general' && (
              <p className="text-xs text-muted-foreground">
                Alcance: {activeConversation.scope_type} - {activeConversation.scope_value || 'todos'}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground hidden sm:inline">
              {user?.name}
            </span>
            <span className="text-xs px-2 py-1 bg-secondary rounded-full capitalize">
              {user?.role}
            </span>
          </div>
        </header>

        {/* Messages Area */}
        <ScrollArea className="flex-1 p-4">
          <div className="max-w-4xl mx-auto space-y-4">
            {messages.length === 0 && !loading ? (
              <WelcomeMessage
                userName={user?.name?.split(' ')[0]}
                onQuickAction={handleQuickAction}
                quickActions={quickActions}
                selectedArea={selectedArea}
              />
            ) : (
              <>
                {messages.map((message, index) => (
                  <ChatMessage
                    key={message.id || index}
                    message={message}
                  />
                ))}

                {sendingMessage && (
                  <div className="flex items-center gap-2 text-muted-foreground p-4">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Analizando datos...</span>
                  </div>
                )}
              </>
            )}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>


      </div>
    </div>
  );
};

export default ChatPage;
